import type { AiAccountDaySpend } from './api.ts'

/**
 * The AI spend table's shape: a row per day, a column per account (#640).
 *
 * Days, not accounts, down the side because the question it answers is "what
 * did each pharmacy cost yesterday, and the day before" -- and there are only
 * ever a handful of pharmacies to read across.
 */
export interface AccountSpendDay {
  readonly day: string
  /** Cost by account id; the key `NO_ACCOUNT` holds the calls that carried none. */
  readonly cost: Readonly<Record<string, number>>
  readonly total: number
}

/** The key under which calls with no account are kept. */
export const NO_ACCOUNT = ''

/** Newest day first, as the screen reads it. */
export function spendByDay(rows: readonly AiAccountDaySpend[]): AccountSpendDay[] {
  const days = new Map<string, Record<string, number>>()
  for (const row of rows) {
    const cost = days.get(row.day) ?? {}
    const key = row.accountId ?? NO_ACCOUNT
    cost[key] = (cost[key] ?? 0) + row.cost
    days.set(row.day, cost)
  }
  return [...days.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([day, cost]) => ({
      day,
      cost,
      total: Object.values(cost).reduce((sum, value) => sum + value, 0),
    }))
}

/** Whether any call went unattributed, so that column shows only when it says something. */
export function hasUnattributed(rows: readonly AiAccountDaySpend[]): boolean {
  return rows.some((row) => row.accountId === null)
}
